import { FormEvent, useState } from 'react'
import { Link } from 'react-router-dom'
import { createPasswordClient } from './lib/supabase'
import { invalidPasswordLink, PasswordFlow, readPasswordLink, requestPasswordLink } from './lib/password-flow'

export function AccountPassword() {
  const [client] = useState(createPasswordClient)
  const [flow] = useState(() => client ? new PasswordFlow(client.auth) : null)
  const [link, setLink] = useState(() => readPasswordLink(window.location.hash))
  const [target, setTarget] = useState<{id: string; email: string} | null>(null)
  const ready = Boolean(target)
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [done, setDone] = useState(false)
  const [busy, setBusy] = useState(false)
  const [email, setEmail] = useState('')
  const [resetBusy, setResetBusy] = useState(false)
  const [resetMessage, setResetMessage] = useState('')
  const [message, setMessage] = useState(!flow ? 'The account service is unavailable.' : link ? 'Continue to verify your personal link and choose your password.' : invalidPasswordLink)

  async function verifyLink() {
    if (!flow || !link || busy) return
    setBusy(true)
    setMessage('Verifying your link…')
    try {
      const verified = await flow.verify(link)
      window.history.replaceState(null, '', window.location.pathname)
      setTarget(verified)
      setMessage('')
      setLink(null)
    } catch {
      setTarget(null)
      setLink(null)
      window.history.replaceState(null, '', window.location.pathname)
      setMessage(invalidPasswordLink)
    }
    finally { setBusy(false) }
  }

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (!flow || busy || !ready) return
    if (password.length < 12) { setMessage('Use at least 12 characters for your password.'); return }
    if (password !== confirm) { setMessage('The passwords do not match.'); return }
    setBusy(true)
    try {
      await flow.save(password)
      window.history.replaceState(null, '', window.location.pathname)
      setPassword('')
      setConfirm('')
      setDone(true)
    } catch (error) {
      const text = error instanceof Error ? error.message : 'We could not save your password. Please try again.'
      if (text === invalidPasswordLink) { setTarget(null); setPassword(''); setConfirm('') }
      setMessage(text)
    }
    finally { setBusy(false) }
  }

  async function requestFreshLink(e: FormEvent) {
    e.preventDefault()
    if (!client || resetBusy) return
    setResetBusy(true); setResetMessage('Requesting a fresh email…')
    try {
      await requestPasswordLink(client.auth, email, window.location.origin)
      setResetMessage('If this address has a website account, a fresh email will arrive shortly. Open the newest email and choose Continue to password setup. Check your spam folder too.')
    } catch {
      setResetMessage('We could not send a fresh link. Check the email address and try again in a minute, or contact siteadmin@ourrenland.com for help.')
    } finally { setResetBusy(false) }
  }

  return <section className="page-section"><div className="panel form-panel local-application">
    <span className="eyebrow dark">Your private account</span><h1>Set your password</h1>
    {done ? <p>Your password for <strong>{target?.email}</strong> has been saved. <Link to="/login">Sign in to the member hall</Link> using that email and your new password.</p> : <>
      {link && !ready && <button type="button" className="button button-dark" onClick={verifyLink} disabled={busy}>{busy ? 'Verifying…' : 'Continue to password setup'}</button>}
      {ready && <form className="form-panel" onSubmit={submit}>
        <p>Setting the password for <strong>{target?.email}</strong>.</p>
        <p>This email link applies only to this account. Your existing member-hall sign-in is separate.</p>
        <label>New password<input type="password" autoComplete="new-password" minLength={12} required value={password} onChange={e => setPassword(e.target.value)} /></label>
        <label>Confirm password<input type="password" autoComplete="new-password" minLength={12} required value={confirm} onChange={e => setConfirm(e.target.value)} /></label>
        <button className="button button-dark" disabled={busy}>Save password</button>
      </form>}
      {message && <p role="status">{message}</p>}
      {!ready && !link && client && <form className="form-panel" onSubmit={requestFreshLink}>
        <h2>Get a fresh password link</h2>
        <p>Enter the email used for your guild account. This works for first-time password setup and forgotten passwords.</p>
        <label>Account email<input type="email" required autoComplete="email" maxLength={254} value={email} onChange={e => setEmail(e.target.value)} disabled={resetBusy}/></label>
        <button className="button button-dark" disabled={resetBusy}>{resetBusy ? 'Sending…' : 'Send a fresh password link'}</button>
        {resetMessage && <p role="status">{resetMessage}</p>}
        <p>Use the newest email promptly. Links expire and can be used only once. For help, email <a href="mailto:siteadmin@ourrenland.com">siteadmin@ourrenland.com</a>.</p>
      </form>}
    </>}
  </div></section>
}
